import { Link } from "@tanstack/react-router";
import {
  Activity,
  AppWindow,
  BookOpenText,
  LayoutDashboard,
  ListTree,
  Radio,
  ScrollText,
  Workflow,
} from "lucide-react";
import { APP_VERSION } from "../lib/version";
import { useMe } from "../lib/auth";
import { canSee, portalRole } from "../lib/rbac";

// Grouped like a CoreUI rail: section labels in caps, brand block on top.
export const NAV_SECTIONS = [
  {
    title: "ตรวจสอบ",
    items: [{ to: "/audit", label: "Audit Log", icon: ScrollText }],
  },
  {
    title: "ภาพรวม",
    items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    title: "การเชื่อมต่อ",
    items: [
      { to: "/clients", label: "Clients", icon: AppWindow },
      { to: "/workflows", label: "Workflows", icon: Workflow },
      { to: "/channels", label: "Channels", icon: Radio },
    ],
  },
  {
    title: "ปฏิบัติการ",
    items: [{ to: "/transactions", label: "Transactions", icon: ListTree }],
  },
  {
    title: "อ้างอิง",
    items: [
      { to: "/api-docs", label: "API Docs", icon: BookOpenText },
      { to: "/health", label: "System Health", icon: Activity },
    ],
  },
] as const;

type NavItem = (typeof NAV_SECTIONS)[number]["items"][number];
export const NAV_ITEMS: readonly NavItem[] = NAV_SECTIONS.flatMap(
  (s) => s.items as readonly NavItem[],
);

export function Sidebar() {
  const { data: me } = useMe();
  const role = me ? portalRole(me) : null;

  // Filter each section's items by the persona, then drop sections left empty
  // (e.g. a Checker has nothing under "การเชื่อมต่อ"). Admin sees all but Audit.
  const sections = NAV_SECTIONS.map((section) => ({
    title: section.title,
    items: section.items.filter((it) => !role || canSee(role, it.to)),
  })).filter((section) => section.items.length > 0);

  return (
    <nav className="sidebar" aria-label="เมนูหลัก">
      <Link to="/dashboard" className="side-brand">
        <img
          className="brand-logo"
          src="/blockfint_logo.svg"
          alt="Blockfint Loan Noti Portal"
        />
        <span className="brand-text">
          <b>Noti Portal</b>
        </span>
      </Link>
      <ul>
        {sections.map((section) => (
          <li key={section.title}>
            <div className="side-section">{section.title}</div>
            <ul>
              {section.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="side-link"
                    activeProps={{ className: "side-link active" }}
                  >
                    <Icon size={17} />
                    <span>{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <div className="sidebar-foot muted small">
        engine: baacloan-notif
        <br />
        v2 · destinations routing
        <br />
        <span title="เวอร์ชัน portal — bump ทุกครั้งที่ deploy">
          Portal {APP_VERSION}
        </span>
      </div>
    </nav>
  );
}
